import { Inbox, type LucideIcon } from "lucide-react";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type LoadingShape = "record" | "list" | "board" | "home";

/**
 * Placeholder while a graph page loads: a skeleton shaped like the page, so
 * the layout does not jump when the data lands. The label is for screen readers.
 */
export function GraphLoading({
  label = "Loading",
  shape = "list",
}: {
  label?: string;
  shape?: LoadingShape;
}) {
  return (
    <div aria-busy="true" aria-live="polite" className="space-y-6">
      <span className="sr-only">{label}…</span>
      {SKELETONS[shape]()}
    </div>
  );
}

function SkeletonCard({ rows = 3 }: { rows?: number }) {
  return (
    <Card className="gap-3 p-5">
      <Skeleton className="h-4 w-28" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton
          key={i}
          className="h-3.5"
          style={{ width: `${90 - i * 12}%` }}
        />
      ))}
    </Card>
  );
}

const SKELETONS: Record<LoadingShape, () => React.ReactNode> = {
  record: () => (
    <>
      <Card className="flex-row items-center gap-4 px-6 py-5">
        <Skeleton className="size-12 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-6 w-48 max-w-full" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
      </Card>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)_minmax(0,22rem)]">
        <div className="space-y-6">
          <SkeletonCard rows={5} />
          <SkeletonCard />
        </div>
        <SkeletonCard rows={6} />
        <div className="hidden space-y-6 xl:block">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    </>
  ),
  list: () => (
    <>
      <Skeleton className="h-8 w-40" />
      <Card className="gap-0 py-0">
        {Array.from({ length: 8 }, (_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 border-b px-4 py-3 last:border-0"
          >
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="ml-auto h-4 w-20" />
          </div>
        ))}
      </Card>
    </>
  ),
  board: () => (
    <>
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-10 w-full max-w-xl" />
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i} className="w-72 shrink-0 gap-3 p-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </Card>
        ))}
      </div>
    </>
  ),
  home: () => (
    <>
      <Skeleton className="h-8 w-64 max-w-full" />
      <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i} className="gap-2 p-5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-7 w-16" />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <SkeletonCard rows={6} />
        </div>
        <SkeletonCard rows={4} />
      </div>
    </>
  ),
};

/** A friendly "nothing here" block: an icon, a line of copy, and an optional action. */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  children,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 px-6 py-10 text-center",
        className,
      )}
    >
      <span className="mb-1 flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-5" aria-hidden />
      </span>
      <p className="text-sm font-medium">{title}</p>
      {children ? (
        <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

interface GraphErrorProps {
  error?: unknown;
  /** What the page shows, for the message: "contact", "deal", ... */
  noun: string;
}

function statusOf(error: unknown): number | undefined {
  return typeof error === "object" && error !== null && "status" in error
    ? Number((error as { status: unknown }).status)
    : undefined;
}

/**
 * A failed or missing record. A 404 is also what another tenant's id looks like
 * (RLS hides it), so the message does not claim the record never existed.
 */
export function GraphError({ error, noun }: GraphErrorProps) {
  const notFound = statusOf(error) === 404 || !error;
  return (
    <Card className="p-6 space-y-2" role="alert">
      <h1 className="text-xl font-semibold">
        {notFound
          ? `This ${noun} is not available`
          : `Could not load this ${noun}`}
      </h1>
      <p className="text-muted-foreground">
        {notFound
          ? "It may have been merged into another record, or you may not have access to it."
          : "Something went wrong on our side. Try again in a moment."}
      </p>
      <Link className="underline" to="/">
        Back to home
      </Link>
    </Card>
  );
}
